"use client";

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@repo/ui";
import { PopoverContainerContext } from "@repo/ui";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@repo/ui";
import { ArrowLeft, ChevronRight } from "lucide-react";
import { FormField } from "@/hooks/schema/use-schema";
import { useEntitySchemaByName } from "@/hooks/schema/use-entity-schema-by-name";
import { AnimatePresence, motion } from "framer-motion";
import { cleanupEntityName } from "@/lib/utils";

type PopoverSide = "top" | "right" | "bottom" | "left";
type PopoverAlign = "start" | "center" | "end";

interface FilterTriggerProps {
  entitySchema:
    | {
        fields: Array<FormField>;
        name: string;
      }
    | undefined;
  onSelect: (field: FormField) => void;
  children?: React.ReactNode;
  asChild?: boolean;
  fieldsToHide?: Array<string>;
  hideFieldTypes?: Array<string>;
  /**
   * When set, a leaf (SCALAR/ENUM) field is only selectable if this returns true
   * for (currentEntityName, fieldName). OBJECT (relation) fields are always kept
   * so the user can still drill deeper to a matching leaf. `entityName` is the
   * entity whose fields are currently listed (`entitySchema.name`). Unset → every
   * field is selectable. Used e.g. to restrict a time-bucket dimension to datetime
   * leaves, resolved via the flat entity schema (the GraphQL type can't tell).
   */
  isLeafFieldAllowed?: (entityName: string | undefined, fieldName: string) => boolean;
  disabled?: boolean;
  amountLevels?: number;
  currentLevel?: number;
  hideListRelations?: boolean;
  hideVariants?: boolean;
  /**
   * IMPORTANT: When this Popover is used inside a modal Radix Dialog, the Dialog disables
   * pointer events outside the dialog content. If PopoverContent is portaled to <body>,
   * it becomes "outside" and can't receive mouse events.
   */
  noPortal?: boolean;
  popoverSide?: PopoverSide;
  popoverAlign?: PopoverAlign;
  popoverSideOffset?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Applied to the inner Command, e.g. to normalize input/item heights at a call site. */
  className?: string;
}

export const FilterTrigger: React.FC<FilterTriggerProps> = ({
  entitySchema,
  onSelect,
  children,
  asChild,
  fieldsToHide,
  hideFieldTypes,
  isLeafFieldAllowed,
  disabled,
  amountLevels,
  hideVariants = true,
  hideListRelations = false,
  noPortal = false,
  popoverSide = "bottom",
  popoverAlign = "end",
  popoverSideOffset,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  className,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);
  const [searchStr, setSearchStr] = useState("");

  // When inside a modal dialog, portal the popover into the dialog node (provided
  // via context) rather than <body> — which the modal marks pointer-events:none,
  // making a body-portaled popover dismiss on click. `noPortal` still wins.
  const portalContainer = useContext(PopoverContainerContext);

  // Use controlled state if provided, otherwise use internal state
  const open = controlledOpen !== undefined ? controlledOpen : internalOpen;
  const setOpen = controlledOnOpenChange || setInternalOpen;

  useEffect(() => {
    if (!open) {
      setSearchStr("");
    }
  }, [open, setSearchStr]);

  // Picking a field hands focus to the new filter's value input; don't pull it back to the trigger.
  const pickedRef = useRef(false);

  const handleClick = async (formField: FormField) => {
    pickedRef.current = true;
    onSelect(formField);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        asChild={asChild}
        disabled={disabled}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {children}
      </PopoverTrigger>
      <PopoverContent
        // See comment in props: make popover interactable inside modal dialogs
        noPortal={noPortal}
        container={portalContainer}
        className="min-w-[var(--radix-popover-trigger-width)] p-0"
        align={popoverAlign}
        side={popoverSide}
        sideOffset={popoverSideOffset}
        onPointerDown={(e) => e.stopPropagation()}
        onCloseAutoFocus={(e) => {
          if (pickedRef.current) e.preventDefault();
          pickedRef.current = false;
        }}
      >
        <EntityCommand
          entitySchema={entitySchema}
          onSelect={handleClick}
          searchStr={searchStr}
          setSearchStr={setSearchStr}
          fieldsToHide={fieldsToHide}
          hideFieldTypes={hideFieldTypes}
          isLeafFieldAllowed={isLeafFieldAllowed}
          amountLevels={amountLevels}
          hideListRelations={hideListRelations}
          hideVariants={hideVariants}
          className={className}
        />
      </PopoverContent>
    </Popover>
  );
};

const EntityCommand: React.FC<
  FilterTriggerProps & {
    searchStr: string;
    setSearchStr: (value: string) => void;
    amountLevels?: number;
    hideListRelations: boolean;
    hideVariants: boolean;
  }
> = ({
  entitySchema,
  onSelect,
  searchStr,
  setSearchStr,
  fieldsToHide,
  hideFieldTypes,
  isLeafFieldAllowed,
  amountLevels,
  hideListRelations,
  hideVariants,
  className,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [currentLevel, setCurrentLevel] = useState(0);

  // --- State to track animation direction ---
  const [direction, setDirection] = useState<"forward" | "backward">("forward");
  const [parents, setParents] = useState<Array<FormField>>([]);

  const navigateBack = useCallback(() => {
    // Set direction before changing page
    setDirection("backward");
    setParents((prev) => prev.slice(0, -1));
    setCurrentLevel((prev) => prev - 1);
  }, []);

  // Scroll to top whenever navigating into a sub-entity.
  // Delay slightly so the scroll happens after the slide animation (120ms)
  // and after cmdk re-renders the new list content.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (ref.current) {
        ref.current.scrollTop = 0;
      }
    }, 150);
    return () => clearTimeout(timer);
  }, [parents.length]);

  const handleClick = (formField: FormField) => {
    setSearchStr("");
    if (formField.kind === "OBJECT") {
      setParents((prev) => [...prev, formField]);
      setDirection("forward");
      setCurrentLevel((prev) => prev + 1);
      return;
    }
    const items = [...parents, formField];
    onSelect({
      ...formField,
      name: items.map((x) => x.name).join("."),
    });
  };

  const getPlaceholderStr = () => {
    const entity =
      parents.length > 0
        ? cleanupEntityName(parents[parents.length - 1].type ?? "")
        : entitySchema?.name;
    return `Search ${entity}...`;
  };

  return (
    <Command
      className={className}
      filter={(value, search) => {
        if (value.toLowerCase().includes(search.toLowerCase())) return 1;
        return 0;
      }}
    >
      <CommandInput
        placeholder={getPlaceholderStr()}
        value={searchStr}
        onValueChange={setSearchStr}
      />
      <CommandList ref={ref}>
        <CommandEmpty className="py-2">Nothing found.</CommandEmpty>
        <AnimatePresence mode="wait" initial={false} custom={direction}>
          <motion.div
            key={parents.length}
            custom={direction}
            // @ts-expect-error - FIXME: why?!
            variants={FilterTriggerAnimationVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
          >
            <CommandGroup>
              {parents.length > 0 ? (
                <>
                  <CommandItem
                    value="back"
                    onSelect={() => navigateBack()}
                    className="text-neutral-600"
                  >
                    <ArrowLeft /> back
                  </CommandItem>
                  <RelationFilterSelect
                    currentLevel={currentLevel}
                    formField={parents[parents.length - 1]}
                    onSelect={handleClick}
                    amountLevels={amountLevels}
                    hideListRelations={hideListRelations}
                    hideVariants={hideVariants}
                    hideFieldTypes={hideFieldTypes}
                    isLeafFieldAllowed={isLeafFieldAllowed}
                  />
                </>
              ) : (
                <EntityFields
                  amountLevels={amountLevels}
                  currentLevel={currentLevel}
                  entitySchema={entitySchema}
                  onSelect={handleClick}
                  fieldsToHide={fieldsToHide}
                  hideFieldTypes={hideFieldTypes}
                  isLeafFieldAllowed={isLeafFieldAllowed}
                  hideListRelations={hideListRelations}
                  hideVariants={hideVariants}
                />
              )}
            </CommandGroup>
          </motion.div>
        </AnimatePresence>
      </CommandList>
    </Command>
  );
};

interface RelationFilterSelectProps {
  onSelect: (formField: FormField) => void;
  // entityName: string;
  formField: FormField;
  fieldsToHide?: Array<string>;
  hideFieldTypes?: Array<string>;
  isLeafFieldAllowed?: (entityName: string | undefined, fieldName: string) => boolean;
  amountLevels?: number;
  currentLevel: number;
  hideListRelations: boolean;
  hideVariants: boolean;
}
const RelationFilterSelect: React.FC<RelationFilterSelectProps> = ({
  onSelect,
  // entityName,
  formField,
  fieldsToHide,
  hideFieldTypes,
  isLeafFieldAllowed,
  amountLevels,
  currentLevel,
  hideListRelations,
  hideVariants,
}) => {
  const entityName = cleanupEntityName(formField.type ?? "");
  const { entitySchemaWithRelations } = useEntitySchemaByName(entityName);

  return (
    <EntityFields
      entitySchema={entitySchemaWithRelations}
      onSelect={onSelect}
      fieldsToHide={fieldsToHide}
      hideFieldTypes={hideFieldTypes}
      isLeafFieldAllowed={isLeafFieldAllowed}
      amountLevels={amountLevels}
      currentLevel={currentLevel}
      hideListRelations={hideListRelations}
      hideVariants={hideVariants}
    />
  );
};

const EntityFields: React.FC<FilterTriggerProps> = ({
  entitySchema,
  onSelect,
  fieldsToHide = [],
  hideFieldTypes,
  isLeafFieldAllowed,
  amountLevels,
  currentLevel,
  hideListRelations,
  hideVariants,
}) => {
  const filterableFields = useMemo(() => {
    let filtered =
      entitySchema?.fields.filter(
        (x) => x.kind === "SCALAR" || x.kind === "OBJECT" || x.kind === "ENUM",
      ) ?? [];
    if (hideVariants) filtered = filtered.filter((x) => x.type !== "FieldVariantsData");
    if (hideFieldTypes?.length)
      filtered = filtered.filter((x) => !hideFieldTypes.includes(x.type ?? ""));
    // Constrain selectable leaves via the caller's predicate, but always keep
    // OBJECT (relation) fields so the user can still drill deeper to a match.
    if (isLeafFieldAllowed)
      filtered = filtered.filter(
        (x) => x.kind === "OBJECT" || isLeafFieldAllowed(entitySchema?.name, x.name),
      );

    return filtered;
  }, [entitySchema, hideVariants, hideFieldTypes, isLeafFieldAllowed]);

  return (
    <>
      {filterableFields
        ?.filter((x) => !fieldsToHide.includes(x.name))
        ?.filter((x) => {
          if (x.kind === "OBJECT" && amountLevels && currentLevel && currentLevel >= amountLevels) {
            return false;
          }
          if (hideListRelations && x.kind === "OBJECT" && x.type?.toLowerCase().includes("list")) {
            return false;
          }
          return true;
        })
        ?.map((f) => (
          <CommandItem
            key={f.name}
            value={f.name}
            onSelect={() => onSelect(f)}
            title={f.name}
            className="flex justify-between gap-2"
          >
            <span className="min-w-0 truncate">{f.name}</span>
            {f.kind === "OBJECT" && <ChevronRight className="shrink-0" />}
          </CommandItem>
        ))}
    </>
  );
};

export const FilterTriggerAnimationVariants = {
  hidden: (direction: "forward" | "backward") => ({
    x: direction === "forward" ? "100%" : "-100%", // Start off-screen right (forward) or left (backward)
    opacity: 0,
  }),
  visible: {
    x: "0%", // End on-screen
    opacity: 1,
    transition: {
      type: "tween", // Use tween for smoother slide
      duration: 0.12, // Adjust duration as needed
      ease: "easeOut", // Adjust easing
    },
  },
  exit: (direction: "forward" | "backward") => ({
    x: direction === "forward" ? "-100%" : "100%", // Exit off-screen left (forward) or right (backward)
    opacity: 0,
    transition: {
      type: "tween",
      duration: 0.12,
      ease: "easeIn",
    },
  }),
};
